'use client';

import {
    FileText,
    Eye,
    GitBranch,
    Shield,
    Sparkles,
    Users,
    FlaskConical,
    LayoutList,
    MonitorSmartphone,
    Building,
    Globe,
    Languages,
    BookOpen,
    Code,
    Zap,
    Wrench,
    Trophy,
    Rocket,
    BookOpenCheck,
    Cloud,
    Scale,
    Boxes,
    Terminal,
    Newspaper,
    Star,
    Video,
    Podcast,
    MessageCircle,
    Mail,
    Rss,
    Medal,
    Plug,
    HeartPulse,
    Landmark,
    Factory,
    RadioTower
} from 'lucide-react';

const hardcodedProductDropdown = {
    columns: [
        {
            items: [
                {
                    label: 'Content Management',
                    href: '#',
                    description: 'Author, organize, and publish at scale',
                    icon: <FileText />
                },
                {
                    label: 'Universal Visual Editor',
                    href: '#',
                    description: 'WYSIWYG editing for any framework',
                    icon: <Eye />
                },
                {
                    label: 'Workflows & Approvals',
                    href: '#',
                    description: 'Automate review, staging, and publishing',
                    icon: <GitBranch />
                },
                {
                    label: 'Security & Compliance',
                    href: '#',
                    description: 'Enterprise-grade security built in',
                    icon: <Shield />
                }
            ]
        },
        {
            items: [
                {
                    label: 'dotAI',
                    href: '#',
                    description: 'AI-powered content generation',
                    icon: <Sparkles />
                },
                {
                    label: 'Personalization',
                    href: '#',
                    description: 'Tailored experiences for every visitor',
                    icon: <Users />
                },
                {
                    label: 'Experiments',
                    href: '#',
                    description: 'A/B test content and measure impact',
                    icon: <FlaskConical />
                },
                {
                    label: 'Feature List',
                    href: '#',
                    description: 'Complete overview of capabilities',
                    icon: <LayoutList />
                }
            ]
        }
    ],
    promo: {
        eyebrow: 'Customer Story',
        title: 'How TELUS manages 50+ sites with dotCMS',
        description: 'Enterprise content ops at telecom scale.',
        bgClass: 'bg-fuschia-100/50 dark:bg-custom-on-background-dark',
        image: '/dA/c09614483e/logo/telus.png?language_id=1/256maxw/8q&w=256&language_id=1',
        cta: { href: '#', linkName: 'Read more' }
    }
};

const hardcodedSolutionsDropdown = {
    columns: [
        {
            heading: 'By Use Case',
            items: [
                {
                    label: 'Websites & Applications',
                    href: '#',
                    description: 'Build fast, scalable digital properties',
                    icon: <MonitorSmartphone />
                },
                {
                    label: 'Customer Portals',
                    href: '#',
                    description: 'Self-service experiences for customers',
                    icon: <Users />
                },
                {
                    label: 'Intranet & Extranets',
                    href: '#',
                    description: 'Internal comms and partner access',
                    icon: <Building />
                },
                {
                    label: 'Multi-site Management',
                    href: '#',
                    description: 'Govern multiple sites from one instance',
                    icon: <Globe />
                },
                {
                    label: 'Multilingual & Localization',
                    href: '#',
                    description: 'Reach global audiences natively',
                    icon: <Languages />
                },
                {
                    label: 'Knowledge Base',
                    href: '#',
                    description: 'Organize and surface documentation',
                    icon: <BookOpen />
                }
            ]
        },
        {
            heading: 'By Industry',
            items: [
                {
                    label: 'Healthcare',
                    href: '/demo-request/healthcare',
                    description: 'Manage healthcare sites with more control.',
                    icon: <HeartPulse />
                },
                {
                    label: 'Financial Services',
                    href: '/demo-request/financial-services',
                    description: 'Enterprise governance with built-in compliance and audit trails.',
                    icon: <Landmark />
                },
                {
                    label: 'Government',
                    href: '/demo-request/government',
                    description: 'WCAG-compliant content with workflows tailored to public sector.',
                    icon: <Scale />
                },
                {
                    label: 'Manufacturing',
                    href: '/demo-request/manufacturing',
                    description: 'Power hundreds of dealer or distributor sites from one platform.',
                    icon: <Factory />
                },
                {
                    label: 'Telecommunications',
                    href: '/demo-request/telecom',
                    description:
                        'Support B2B and B2C content at scale with real-time personalization.',
                    icon: <RadioTower />
                }
            ]
        },
        {
            heading: 'By Role',
            items: [
                {
                    label: 'Developers',
                    href: '#',
                    description: 'APIs, SDKs, and extensibility',
                    icon: <Code />
                },
                {
                    label: 'Marketers',
                    href: '#',
                    description: 'Launch campaigns without engineering',
                    icon: <Zap />
                },
                {
                    label: 'DevOps',
                    href: '#',
                    description: 'CI/CD, containers, and infra control',
                    icon: <Wrench />
                },
                {
                    label: 'Business Leaders',
                    href: '#',
                    description: 'ROI, governance, and scale',
                    icon: <Trophy />
                }
            ]
        },
        {
            heading: 'By Need',
            items: [
                {
                    label: 'dotEvergreen',
                    href: '#',
                    description: 'Always up-to-date managed service',
                    icon: <Rocket />
                },
                {
                    label: 'Support & Services',
                    href: '#',
                    description: 'Expert help when you need it',
                    icon: <BookOpenCheck />
                },
                {
                    label: 'Integrations',
                    href: '#',
                    description: 'Connect your existing tech stack',
                    icon: <Boxes />
                }
            ]
        }
    ],
    promo: {
        eyebrow: 'Solution Spotlight',
        title: 'SOC 2 compliance at enterprise scale',
        description: 'Security without sacrificing content velocity.',
        bgClass: 'bg-gray-100/50 dark:bg-custom-on-background-dark',
        image: '/dA/c09614483e/logo/telus.png?language_id=1/256maxw/8q&w=256&language_id=1',
        cta: { href: '#', linkName: 'Read more' }
    }
};

const hardcodedDevelopersDropdown = {
    columns: [
        {
            heading: 'Tools & SDKs',
            items: [
                {
                    label: 'Developer Site',
                    href: 'https://dev.dotcms.com/',
                    description: 'Docs, tutorials, and API reference',
                    icon: <Code />
                },
                {
                    label: 'SDKs & Dev Tools',
                    href: '#',
                    description: 'Libraries for React, Angular, and more',
                    icon: <Wrench />
                },
                {
                    label: 'API Playground',
                    href: '#',
                    description: 'Explore and test the API interactively',
                    icon: <Terminal />
                },
                {
                    label: 'dotCLI',
                    href: '#',
                    description: 'Command-line tooling for dotCMS',
                    icon: <Terminal />
                }
            ]
        },
        {
            heading: 'Getting Started',
            items: [
                {
                    label: 'Get Started Now',
                    href: '#',
                    description: 'Quickstart guide for new developers',
                    icon: <Rocket />
                },
                {
                    label: 'Business Source License',
                    href: '#',
                    description: 'Open licensing model',
                    icon: <Scale />
                },
                {
                    label: 'dotCMS MCP Server',
                    href: '/product/dotcms-mcp',
                    description: 'Connect AI agents to dotCMS via Model Context Protocol',
                    icon: <Plug />
                }
            ]
        },
        {
            heading: 'Deployments',
            items: [
                {
                    label: 'Cloud Anywhere',
                    href: '#',
                    description: 'Run dotCMS on any cloud provider',
                    icon: <Globe />
                },
                {
                    label: 'Flexible Deployments',
                    href: '#',
                    description: 'Cloud, hybrid, and on-prem options',
                    icon: <Cloud />
                }
            ]
        }
    ],
    promo: {
        eyebrow: 'New Guide',
        title: 'Build a headless storefront in 30 minutes',
        description: 'Step-by-step with React + dotCMS APIs.',
        bgClass: 'bg-gray-100/50 dark:bg-custom-on-background-dark',
        image: '/dA/c09614483e/logo/telus.png?language_id=1/256maxw/8q&w=256&language_id=1',
        cta: { href: '#', linkName: 'Read more' }
    }
};

const hardcodedResourcesDropdown = {
    columns: [
        {
            items: [
                {
                    label: 'Case Studies',
                    href: '#',
                    description: 'See how enterprises use dotCMS',
                    icon: <Trophy />
                },
                {
                    label: 'Articles',
                    href: '#',
                    description: 'Insights on content and digital strategy',
                    icon: <Newspaper />
                },
                {
                    label: 'Customer Reviews',
                    href: '#',
                    description: 'What real users are saying',
                    icon: <Star />
                },
                {
                    label: 'Videos',
                    href: '#',
                    description: 'Demos, webinars, and tutorials',
                    icon: <Video />
                },
                {
                    label: 'Real Talk Real Trust Podcast',
                    href: '#',
                    description: 'Candid conversations with industry leaders',
                    icon: <Podcast />
                }
            ]
        },
        {
            items: [
                {
                    label: 'Join our Community',
                    href: '#',
                    description: 'Connect with other dotCMS users',
                    icon: <MessageCircle />
                },
                {
                    label: 'Subscribe to dotDEV',
                    href: '#',
                    description: 'Developer news and technical updates',
                    icon: <Mail />
                },
                {
                    label: 'Subscribe to dotNEWS',
                    href: '#',
                    description: 'Product updates and company news',
                    icon: <Rss />
                },
                {
                    label: 'Recognition',
                    href: '#',
                    description: 'Awards and recognition for dotCMS',
                    icon: <Medal />
                },
                {
                    label: 'Newsroom',
                    href: '/news',
                    description: 'Latest news and press releases from dotCMS',
                    icon: <Newspaper />
                }
            ]
        }
    ],
    promo: {
        eyebrow: 'Trending',
        title: 'Why headless wins in regulated industries',
        description: "Latest podcast with HSBC's Head of Digital.",
        bgClass: 'bg-gray-100/30 dark:bg-custom-on-background-dark',
        image: '/dA/c09614483e/logo/telus.png?language_id=1/256maxw/8q&w=256&language_id=1',
        cta: { href: '#', linkName: 'Read more' }
    }
};

export const hardcodedDropdownData = {
    product: hardcodedProductDropdown,
    solutions: hardcodedSolutionsDropdown,
    developers: hardcodedDevelopersDropdown,
    resources: hardcodedResourcesDropdown
};
